import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import { UPPERCASE_MAKES_AND_MODLES } from "@/constants/vehicles";
import { ActivityModel } from "@/models/pipeline";
import moment from "moment";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns an ISO-8601-like timestamp for the given date expressed in UK
 * (Europe/London) wall-clock time, e.g. "2026-07-14T14:30:00".
 *
 * `Date.toISOString()` always emits UTC (with a trailing "Z"), so during
 * British Summer Time it reads 1 hour behind local UK time. The backend stores
 * the value verbatim, which is why timestamps appeared an hour early. Use this
 * helper for any date sent in an API payload so the server records UK time.
 */
export const toUkIsoString = (date: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}`;
};

export const formatDate = (date: Date | string) => {
  if (!date) return "";
  return moment(date).format("DD/MM/YYYY");
};

export const formatDateTime = (date: Date | string) => {
  return moment(date).format("DD MMM, HH:mm");
};

export const formatDateRelative = (date: Date | string) => {
  return moment(date).fromNow();
};

export const formatDateRelativeLong = (date: Date | string) => {
  const now = moment();
  const inputDate = moment(date);
  const duration = moment.duration(now.diff(inputDate));
  const years = Math.floor(duration.asYears());
  const months = Math.floor(duration.asMonths() % 12);
  const days = Math.floor(duration.asDays());
  const hours = Math.floor(duration.asHours());

  if (years > 0 && months > 0) {
    return `${years} year${years !== 1 ? "s" : ""} ${months} month${months !== 1 ? "s" : ""} ago`;
  } else if (years > 0) {
    return `${years} year${years !== 1 ? "s" : ""} ago`;
  } else if (months > 0) {
    return `${months} month${months !== 1 ? "s" : ""} ago`;
  } else if (days > 0) {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  } else {
    return moment(date).fromNow();
  }
};

export const formatDateDaysOld = (date: Date | string, longLabel: boolean = false) => {
  const lastTouchDate = new Date(date);
  const currentDate = new Date();
  const diffTime = currentDate.getTime() - lastTouchDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffTime / (1000 * 60));

  if (diffMinutes < 60) {
    return longLabel ? `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""}` : `${diffMinutes}m`;
  } else if (diffHours < 24) {
    return longLabel ? `${diffHours} hour${diffHours !== 1 ? "s" : ""}` : `${diffHours}h`;
  } else {
    return longLabel ? `${diffDays} day${diffDays !== 1 ? "s" : ""}` : `${diffDays}d`;
  }
};

export const isTodaysDate = (date: Date) => {
  return moment(date).isSame(moment(), "day");
};

export const formatTitleHours = (hour: number) => {
  return moment().hour(hour).minute(0).format("h:mm A");
};

export const formatCurrency = (value: number, dp: number = 2) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  }).format(value);
};

export const formatNumber = (value: number) => {
  return new Intl.NumberFormat("en-GB").format(value);
};

// export const triggerCall = (number: string) => {
//   Flex.Actions.invokeAction("StartOutboundCall", {
//     destination: number,
//   });
// };

export const getFullUrl = (url: string): string => {
  if (!url) return "#";
  return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
};

export const getMostRecentActivity = (activities: ActivityModel[], lastTouch?: string | null): ActivityModel | null => {
  if (!activities || activities.length === 0) return null;

  const filteredActivities = filterActivitiesSinceLastTouch(activities, lastTouch);
  if (filteredActivities.length === 0) return null;

  return filteredActivities.reduce((latest, current) => {
    const currentTime = new Date(current.activityTime);
    const latestTime = new Date(latest.activityTime);
    return currentTime > latestTime ? current : latest;
  });
};

export const filterActivitiesSinceLastTouch = (
  activities: ActivityModel[],
  lastTouch?: string | null
): ActivityModel[] => {
  if (!activities || activities.length === 0) return [];
  if (!lastTouch) return activities;

  const lastTouchDate = new Date(lastTouch);
  return activities.filter((activity) => {
    const activityDate = new Date(activity.activityTime);
    return activityDate > lastTouchDate;
  });
};

export const formatNewActivity = (activities: ActivityModel[], lastTouch?: string | null): string => {
  if (!activities || activities.length === 0) return "";

  const mostRecentActivity = getMostRecentActivity(activities, lastTouch);
  if (!mostRecentActivity) return "";

  const timeSince = formatDateDaysOld(mostRecentActivity.activityTime, true);

  return `${mostRecentActivity.activityTypeName} (${timeSince} ago)`;
};

export const formatNewActivityShort = (activities: ActivityModel[], lastTouch?: string | null): string => {
  if (!activities || activities.length === 0) return "";

  const mostRecentActivity = getMostRecentActivity(activities, lastTouch);
  if (!mostRecentActivity) return "";

  const timeSince = formatDateDaysOld(mostRecentActivity.activityTime, false);

  return `${timeSince} ago`;
};

export const toNormalCase = (text: string, minLength: number = 4): string => {
  if (!text) return "";

  if (text.length < minLength) return text;

  const words = text.split(/([^\w]+)/g);

  return words
    .map((word) => {
      if (!word || /[^\w]/.test(word)) return word;

      const upperWord = word.toUpperCase();
      if (UPPERCASE_MAKES_AND_MODLES.includes(upperWord)) {
        return upperWord;
      }

      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join("");
};
